import { Alert, Box, CircularProgress, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useListSecurityControlsQuery } from "../../../hooks/useListSecurityControlsQuery";
import ConnectedAccount from "../../../models/ConnectedAccount";
import Loader from "../../common/Loader";
import StyledTableCell from "../../common/StyledTableCell";
import FTRDisruptiveControlsRow from "./FTRDisruptiveControlsRow";
import LoadingButton from "@mui/lab/LoadingButton";

interface FTRDisruptiveControlsProps {
	technicalReviewArn: string;
	connectedAccounts: Array<ConnectedAccount>;
	setUnlockSteps: React.Dispatch<
		React.SetStateAction<{
			step2: boolean;
			step3: boolean;
			step4: boolean;
			step5: boolean;
		}>
	>;
	changeTab: (index: number) => void;
}

type RowQuestion = {
  controlname: string;
  controldescription: string;
  compliancestatus: string;
	remediatestatus: string;
	remediationestimate: string;
	remediationUrl: string;
	intrusive: string;
	controlshlink: string;
};

/**
 * All Failed Disruptive Recommendations of FTR and CIS 1.4 selected
 * @param technicalReviewArn technical reveiew arn that was selected when creating the framework
 * @param connectedAccounts state of connectedAccounts information
 * @param setUnlockSteps which step to unlock and lock in the tabs
 * @param changeTab tab to switch to after this page is completed
 * @returns all Failed Disruptive Recommendations of FTR and CIS 1.4 selected
 */
const FTRDisruptiveControls: FC<FTRDisruptiveControlsProps> = ({
	technicalReviewArn,
	connectedAccounts,
	setUnlockSteps,
	changeTab
}: FTRDisruptiveControlsProps) => {
	const [rows, setRows] = useState<Array<RowQuestion>>([]);
	const scrollRef = useRef<null | HTMLDivElement>(null);

	const { controls, loading } = useListSecurityControlsQuery({
		arn: technicalReviewArn,
		connectedAccounts,
	});

	//Handle that unlocks step 5
	const handleUnlockSteps = useCallback(() => {
		setUnlockSteps((prevState) => ({
			...prevState,
			step4: false, // Lock step 4
			step5: true, // Unlock step 5
		}));
	}, []);

	const handleSubmit = useCallback(() => {
		handleUnlockSteps();
		changeTab(4);
	}, [handleUnlockSteps, changeTab]);

	useEffect(() => {
		const filteredControls = controls.filter((control: any) => {
			const standards = control?.customStandard;
			return (
				Array.isArray(standards) &&
				standards.length > 0 &&
				control?.intrusive !== "NON-INTRUSIVE" &&
				control?.compliancestatus === "FAILED" &&
				(
					standards.includes("CIS Benchmark for CIS Amazon Web Services Foundations Benchmark, v1.4.0, Level 1 and 2") ||
					standards.includes("Foundational Technical Review")
				)
			);
		});

		const rowMap = new Map();
		filteredControls.forEach((control: any) => {
			const key = control.controlname; //AWS Control
			if (!rowMap.has(key)) {
				rowMap.set(key, {
					controlname: control.controlname,
					controldescription: control.controldescription,
					compliancestatus: control.compliancestatus,
					remediatestatus: control.remediatestatus,
					remediationestimate: control.remediationestimate,
					remediationUrl: control.remediationUrl,
					intrusive: control.intrusive,
					controlshlink: control.controlshlink,
				});
			}
		});
		// Convert Map to Array and determine final compliance status
		const data = Array.from(rowMap.values()).map(item => ({
			...item,
		}))
		.sort((a, b) => a.controlname.localeCompare(b.controlname)); // Sort by frameworkControl
		setRows(data);		
	}, [controls, loading]);

	return (
		<Grid
			container
			sx={{ p: 2 }}
			spacing={2}
			alignItems="stretch"
			flexDirection={"column"}
			component={Paper}
			maxWidth="lg"
		>
			<Grid container flexDirection="row" sx={{pb:2}}>
				<Alert variant="outlined" severity="info">
					Controls will need to be manually remediated. Click (Material UI info Icon) for more information and remediation instructions. If no failed controls are shown below click Next.
				</Alert>
			</Grid>
			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
								<StyledTableCell />
                <StyledTableCell align="center">Control ID</StyledTableCell>
                <StyledTableCell align="center">Description</StyledTableCell>
                <StyledTableCell align="center">Compliance Status</StyledTableCell>
								<StyledTableCell align="center">Findings</StyledTableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading && (
							<TableRow
								key="FTR-DisruptiveRecommendations-loader"
								sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
							>
								<TableCell colSpan={12}>
									<Loader/>
								</TableCell>
							</TableRow>
						)}
						{rows.map((row, index) =>(
							<FTRDisruptiveControlsRow
								key={row.controlname}
								index={index}
								technicalReviewArn={technicalReviewArn}
								row={row}
								connectedAccounts={connectedAccounts}
							/>
						))}
					</TableBody>
				</Table>
			</TableContainer>
			<Grid item xs md={12}>
				<Box ref={scrollRef} sx={{ display: "flex", justifyContent: "flex-end" }}>
					<LoadingButton
						variant="contained"
						color="secondary"
						type="submit"
						disabled={loading}
						loading={loading}
						onClick={handleSubmit}
						sx={{
							width: 157,
							height: 40,
							bgcolor: "secondary.main",
							"&:hover": {
								bgcolor: "secondary.main",
								color: "secondary.contrastText",
							},
						}}
						loadingIndicator={
							<CircularProgress color={"secondary"} size={22} />
						}
					>
						Next
					</LoadingButton>
				</Box>
			</Grid>
		</Grid>
	)
};

export default FTRDisruptiveControls;